import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { StoreOption } from './Index';

type Props = {
    options: StoreOption[];
    value: number;
    onChange: (id: number) => void;
    placeholder?: string;
};

type Rect = { left: number; top: number; width: number; height: number };

export default function StoreSelect({ options, value, onChange, placeholder = 'No store' }: Props) {
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState<Rect | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);

    const selected = useMemo(
        () => options.find(o => o.id === value),
        [options, value]
    );

    function measure() {
        const el = rootRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    }

    useLayoutEffect(() => {
        if (open) measure();
    }, [open]);

    useEffect(() => {
        function onDoc(e: MouseEvent) {
            const t = e.target as HTMLElement | null;
            if (t && t.closest('.store-select-portal__list')) return;
            if (!rootRef.current) return;
            if (!rootRef.current.contains(t as Node)) setOpen(false);
        }
        function onScroll() { if (open) measure(); }
        function onResize() { if (open) measure(); }
        document.addEventListener('mousedown', onDoc, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize, true);
        return () => {
            document.removeEventListener('mousedown', onDoc, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize, true);
        };
    }, [open]);

    const portal = open && rect
        ? createPortal(
            <Dropdown
                rect={rect}
                options={options}
                value={value}
                onChange={(id) => { onChange(Number(id)); setOpen(false); }}
                placeholder={placeholder}
            />,
            document.body
        )
        : null;

    return (
        <div className="store-select" ref={rootRef}>
            <button
                type="button"
                className="control store-select__trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            >
                {value === 0 || !selected ? (
                    <span className="muted">{placeholder}</span>
                ) : selected?.logo ? (
                    <>
                        <img className="avatar" src={selected.logo} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        <span>{selected.name}</span>
                    </>
                ) : (
                    <span>{selected?.name}</span>
                )}
                <span className="caret">▾</span>
            </button>
            {portal}
        </div>
    );
}

function Dropdown({
    rect,
    options,
    value,
    onChange,
    placeholder,
}: {
    rect: Rect;
    options: StoreOption[];
    value: number;
    onChange: (id: number) => void;
    placeholder: string;
}) {
    const maxHeight = Math.min(260, Math.floor(window.innerHeight * 0.6));
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const openUp = spaceBelow < 200;
    const top = openUp ? rect.top - 6 - maxHeight : rect.top + rect.height + 6;

    const listStyle: React.CSSProperties = {
        position: 'fixed',
        left: rect.left,
        top,
        width: rect.width,
        maxHeight,
        zIndex: 12001,
        pointerEvents: 'auto'
    };

    const portalStyle: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        pointerEvents: 'none'
    };

    return (
        <div style={portalStyle} className="store-select-portal">
            <div role="listbox" className="store-select-portal__list" style={listStyle} tabIndex={-1}>
                <button
                    role="option"
                    aria-selected={value === 0}
                    className={`store-select__item ${value === 0 ? 'selected' : ''}`}
                    onClick={() => onChange(0)}
                >
                    <span className="muted">{placeholder}</span>
                </button>
                {options.map(o => {
                    const showLogo = typeof o.logo === 'string' && o.logo.trim().length > 0;
                    return (
                        <button
                            key={o.id}
                            role="option"
                            aria-selected={o.id === value}
                            className={`store-select__item ${o.id === value ? 'selected' : ''}`}
                            onClick={() => onChange(o.id)}
                            title={o.name}
                        >
                            {showLogo && <img className="avatar" src={o.logo} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} loading="lazy" />}
                            <span>{o.name}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
